// Text for the patient page panels that came from the Manus patient dashboard (approved
// 2026-11-02): what the hospital systems recorded, in neutral words. Pure: the page passes the
// encounter view in.
//
// Risk, Priority Score, trajectory and contributors (approved 2026-11-03, CLAUDE.md hard rule 3
// exception): on the synthetic site only, each visit shows the #/demo stage values of the Manus
// scenario, chosen by recorded milestones (arrival, first observations, first lab result,
// observations after it), never computed from any value. Other sites show "not configured".

import type {
  BoardEntry,
  ObservationSet,
  ObservationValue,
  OrderItem,
  TimelineItem,
} from '@diagnuvo/domain';
import { clockTime } from './board-state.ts';
import { COPY, STAGES, type Stage, type Vital } from './scenario/stages.ts';

/** Titles shared with #/demo, which shows the handoff pack's fixed values instead. */
export const NOT_CONFIGURED = {
  riskTitle: 'Clinical Deterioration Risk',
  scoreTitle: 'Diagnuvo Priority Score',
  value: 'Not configured',
  text: 'No validated model. Diagnuvo calculates no risk, score or prognosis for live patients.',
} as const;

/** The same horizons as #/demo, each shown as not configured. */
export const PROGNOSTIC_HORIZONS = [
  'ICU-admission probability · 6-hour horizon',
  'ICU-admission probability · 24-hour horizon',
  'In-hospital mortality probability · 6-hour horizon',
  'In-hospital mortality probability · 24-hour horizon',
] as const;

/** Source-reported scores are shown as sent elsewhere; they are not compared here. */
const NOT_COMPARED = new Set(['NEWS2']);

/** The first number in a value as sent ("104 /min" → 104), or undefined. */
function numberIn(value: string): number | undefined {
  const match = /-?\d+(?:\.\d+)?/.exec(value);
  return match === null ? undefined : Number(match[0]);
}

export type ObservationChange =
  | { kind: 'higher' | 'lower' | 'changed'; label: string; value: string; previous: string }
  | { kind: 'unchanged'; label: string; value: string }
  | { kind: 'first'; label: string; value: string }
  | { kind: 'missing'; label: string; previous: string };

/** Each value of the latest set against the set before it, by code. */
export function observationChanges(
  latest: ObservationSet,
  previous: ObservationSet | undefined,
): ObservationChange[] {
  const compared = (v: ObservationValue) => !NOT_COMPARED.has(v.code);
  const before = new Map((previous?.values ?? []).filter(compared).map((v) => [v.code, v]));
  const changes: ObservationChange[] = latest.values.filter(compared).map((v) => {
    const old = before.get(v.code);
    if (old === undefined) return { kind: 'first', label: v.label, value: v.value };
    if (old.value === v.value) return { kind: 'unchanged', label: v.label, value: v.value };
    const [a, b] = [numberIn(old.value), numberIn(v.value)];
    const kind =
      a === undefined || b === undefined || a === b ? 'changed' : b > a ? 'higher' : 'lower';
    return { kind, label: v.label, value: v.value, previous: old.value };
  });
  const now = new Set(latest.values.map((v) => v.code));
  for (const [code, old] of before)
    if (!now.has(code)) changes.push({ kind: 'missing', label: old.label, previous: old.value });
  return changes;
}

function changeSentence(c: ObservationChange): string {
  switch (c.kind) {
    case 'higher':
    case 'lower':
      return `${c.label} is ${c.kind}: ${c.value}, previously ${c.previous}.`;
    case 'changed':
      return `${c.label} changed: ${c.value}, previously ${c.previous}.`;
    case 'unchanged':
      return `${c.label} is unchanged at ${c.value}.`;
    case 'first':
      return `${c.label} is recorded for the first time: ${c.value}.`;
    case 'missing':
      return `${c.label} is not in this set (previously ${c.previous}).`;
  }
}

/** "Dynamic clinical state": the latest recorded values and how they changed. Observation only. */
export function physiologyText(
  latest: ObservationSet | undefined,
  previous: ObservationSet | undefined,
): string {
  if (latest === undefined) return 'No observations have been recorded for this visit yet.';
  const values = latest.values.filter((v) => !NOT_COMPARED.has(v.code));
  if (previous === undefined) {
    const list = values.map((v) => `${v.label} ${v.value}`).join(', ');
    return `The first recorded observations (${clockTime(latest.at)}): ${list}. There is no earlier set to compare with.`;
  }
  const sentences = observationChanges(latest, previous).map(changeSentence);
  return `Recorded at ${clockTime(latest.at)}, compared with ${clockTime(previous.at)}: ${sentences.join(' ')}`;
}

/** The latest recorded event in one line: time, what it was, and which system sent it. */
export function latestEvent(timeline: readonly TimelineItem[], sourceLabel: (s: string) => string) {
  const item = timeline.at(-1);
  if (item === undefined) return undefined;
  return {
    at: clockTime(item.at),
    what: item.detail === undefined ? item.title : `${item.title} · ${item.detail}`,
    source: sourceLabel(item.source),
  };
}

/** Orders without a result or report yet: the same rule as the board's "Results pending". */
export function pendingResults(labs: readonly OrderItem[], imaging: readonly OrderItem[]) {
  return {
    labs: labs.filter((l) => ['ordered', 'received', 'preliminary'].includes(l.status)).length,
    imaging: imaging.filter((i) => !i.status.startsWith('reported')).length,
  };
}

/** Sites whose patient pages show the predefined demonstration values: synthetic data only. */
export const DEMO_VALUE_SITES: ReadonlySet<string> = new Set(['SYN']);

/** The label on every predefined value on a live patient page. */
export const demoValueLabel = (stage: Stage): string =>
  `Predefined demonstration value (Manus scenario Stage ${stage.number}). Not calculated from this patient's data. ${COPY.outputBoundary}`;

/** A #/demo stage reached by this visit, at the clinical time of its milestone. */
export interface DemoStage {
  stage: Stage;
  at: string;
}

/**
 * The #/demo stages this visit has reached: stage n at the clinical time of milestone n
 * (EncounterState.milestones: arrival, first observations, first lab result after them,
 * observations after that result). Recorded events only; no value decides anything.
 */
export function demoStages(milestones: readonly string[] | undefined): DemoStage[] {
  return (milestones ?? []).flatMap((at, i) => {
    const stage = STAGES[i];
    return stage === undefined ? [] : [{ stage, at }];
  });
}

/** The stage for a milestone count from a board row (1 to 4), or undefined. */
export const demoStageOf = (count: number | undefined): Stage | undefined =>
  count === undefined ? undefined : STAGES[count - 1];

export type DemoStatus = 'Critical' | 'Urgent' | 'Stable';

/**
 * The Manus status band of a predefined Priority Score: Stable under 20, Urgent 20 to 69,
 * Critical 70 and over (approved 2026-11-03). A label of a fixed value, not an assessment.
 */
export function demoStatus(score: number): DemoStatus {
  return score >= 70 ? 'Critical' : score >= 20 ? 'Urgent' : 'Stable';
}

const CODES: Readonly<Record<Vital['key'], readonly string[]>> = {
  gcs: ['GCS'],
  hr: ['HR'],
  bp: ['SBP', 'DBP'],
  rr: ['RR'],
  spo2: ['SPO2'],
};
const LABELS: Readonly<Record<Vital['key'], string>> = {
  gcs: 'GCS',
  hr: 'HR',
  bp: 'BP',
  rr: 'RR',
  spo2: 'SpO2',
};

/** The stage's contributor signals, each with this patient's latest value as sent. */
export function liveContributors(stage: Stage, latest: ObservationSet | undefined) {
  const valueOf = (code: string) => latest?.values.find((v) => v.code === code)?.value;
  return stage.contributors.map((key) => {
    let value: string | undefined;
    if (key === 'bp') {
      const [sbp, dbp] = [valueOf('SBP'), valueOf('DBP')];
      value =
        sbp !== undefined && dbp !== undefined
          ? `${numberIn(sbp) ?? ''}/${numberIn(dbp) ?? ''} mmHg`
          : sbp !== undefined
            ? `${sbp} systolic`
            : dbp !== undefined
              ? `${dbp} diastolic`
              : undefined;
    } else {
      value = valueOf(CODES[key][0] ?? '');
    }
    return value === undefined
      ? { key, label: LABELS[key], value, tag: 'Not recorded' }
      : { key, label: LABELS[key], value, tag: COPY.contributorObservation };
  });
}

/** A board row's predefined stage and status band, or undefined before any milestone. */
export function rowDemo(row: BoardEntry): { stage: Stage; status: DemoStatus } | undefined {
  const stage = demoStageOf(row.milestones);
  return stage === undefined ? undefined : { stage, status: demoStatus(stage.priorityScore) };
}
